import { useMemo, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { McpKeyRow } from '@vidmakr/bindings/types';
import { Bot, Copy, KeyRound, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Card, ConfirmModal, Field, Input, Menu, Modal, Select, Tabs, type MenuItem } from './ui';
import { api } from '@/lib/api';
import { timeAgo, tsDate } from '@/lib/format';
import { reducers } from '@/lib/stdb';
import { attempt, errorMessage, toast } from '@/lib/toast';

interface CreatedKey {
  id: string;
  name: string;
  key: string;
  mcpUrl: string;
}

const day = (ts: McpKeyRow['createdAt'] | undefined) => tsDate(ts)?.toLocaleDateString() ?? '';
const copy = (text: string, what: string) => void navigator.clipboard.writeText(text).then(() => toast.success(`${what} copied`));

function Snippet({ label, text, what }: { label: string; text: string; what: string }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</span>
        <Button size="sm" onClick={() => copy(text, what)}>
          <Copy size={14} /> Copy
        </Button>
      </div>
      <pre className="selectable max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-bg p-3 font-mono text-xs">{text}</pre>
    </div>
  );
}

/** How to connect an MCP client with a freshly created key (shown once). */
function KeySetup({ k }: { k: CreatedKey }) {
  const [tab, setTab] = useState<'code' | 'desktop' | 'web'>('code');
  const claudeCode = `claude mcp add --transport http vidmakr ${k.mcpUrl} --header "Authorization: Bearer ${k.key}"`;
  const desktop = JSON.stringify(
    {
      mcpServers: {
        vidmakr: {
          command: 'npx',
          args: ['-y', 'mcp-remote', k.mcpUrl, '--header', 'Authorization:${VIDMAKR_AUTH}'],
          env: { VIDMAKR_AUTH: `Bearer ${k.key}` },
        },
      },
    },
    null,
    2,
  );
  const urlWithKey = `${k.mcpUrl}/${k.key}`;
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        This key is shown once. Anyone with it can do everything you can do in vidmakr, in every organization you belong to, until you delete it.
      </p>
      <Snippet label="MCP key" text={k.key} what="Key" />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'code', label: 'Claude Code' },
          { id: 'desktop', label: 'Claude Desktop' },
          { id: 'web', label: 'Claude.ai / other' },
        ]}
      />
      {tab === 'code' ? (
        <Snippet label="Run in a terminal" text={claudeCode} what="Command" />
      ) : tab === 'desktop' ? (
        <>
          <Snippet label="Add to claude_desktop_config.json" text={desktop} what="Config" />
          <p className="text-xs text-muted">Settings → Developer → Edit config, paste the server into mcpServers, then restart Claude Desktop. Needs Node.js.</p>
        </>
      ) : (
        <>
          <Snippet label="Server URL (Streamable HTTP)" text={k.mcpUrl} what="URL" />
          <p className="text-xs text-muted">
            Clients that can send headers: use this URL with the header <code className="font-mono">Authorization: Bearer {'<key>'}</code>.
          </p>
          <Snippet label="URL with the key in it" text={urlWithKey} what="URL" />
          <p className="text-xs text-muted">
            For clients that only take a URL, such as custom connectors in Claude.ai (Settings → Connectors → Add custom connector). Keep this URL as secret as
            the key. The server must be reachable from the internet.
          </p>
        </>
      )}
    </div>
  );
}

/** Personal MCP keys: let AI assistants such as Claude edit and export videos as you. */
export function McpKeysCard() {
  const [rows] = useTable(tables.myMcpKeys);
  const keys = useMemo(() => [...rows].sort((a, b) => Number(b.createdAt.microsSinceUnixEpoch - a.createdAt.microsSinceUnixEpoch)), [rows]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [days, setDays] = useState('0');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<McpKeyRow | null>(null);

  const create = async () => {
    setCreating(true);
    try {
      setCreated(await api<CreatedKey>('/api/mcp-keys', { method: 'POST', json: { name, expiresInDays: Number(days) } }));
      setAdding(false);
      setName('');
      setDays('0');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const items = (k: McpKeyRow): (MenuItem | 'sep')[] => [
    { label: 'Rename', icon: <Pencil size={14} />, onClick: () => setRenaming({ id: k.id, name: k.name }) },
    'sep',
    { label: 'Delete', icon: <Trash2 size={14} />, danger: true, onClick: () => setDeleting(k) },
  ];
  const expired = (k: McpKeyRow) => !!k.expiresAt && (tsDate(k.expiresAt)?.getTime() ?? 0) < Date.now();

  return (
    <Card className="mt-5 max-w-xl p-5">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold">
          <Bot size={18} /> MCP keys
        </div>
        <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
          <Plus size={14} /> New key
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted">
        Connect Claude or another AI assistant over the Model Context Protocol. It can then create projects, import media, edit timelines and export
        videos as you.
      </p>
      {keys.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-faint">No keys yet</div>
      ) : (
        <div className="divide-y divide-line rounded-lg border border-line">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center gap-3 px-3 py-2.5">
              <KeyRound size={16} className="shrink-0 text-muted" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className="truncate">{k.name}</span>
                  {expired(k) ? <Badge tone="danger">expired</Badge> : null}
                </div>
                <div className="text-xs text-faint">
                  <span className="font-mono">{k.prefix}…</span> · created {day(k.createdAt)} ·{' '}
                  {k.lastUsedAt ? `used ${timeAgo(k.lastUsedAt)}` : 'never used'}
                  {k.expiresAt && !expired(k) ? ` · expires ${day(k.expiresAt)}` : ''}
                </div>
              </div>
              <Menu
                trigger={
                  <Button variant="ghost" size="icon-sm" aria-label="Key actions">
                    <MoreHorizontal size={15} />
                  </Button>
                }
                items={items(k)}
              />
            </div>
          ))}
        </div>
      )}

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="New MCP key"
        footer={
          <Button variant="primary" disabled={!name.trim()} loading={creating} onClick={() => void create()}>
            Create key
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Name" hint="Where you will use it">
            <Input
              value={name}
              autoFocus
              maxLength={80}
              placeholder="Claude on my laptop"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && name.trim() && void create()}
            />
          </Field>
          <Field label="Expires">
            <Select value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="0">Never</option>
              <option value="7">In 7 days</option>
              <option value="30">In 30 days</option>
              <option value="90">In 90 days</option>
              <option value="365">In a year</option>
            </Select>
          </Field>
        </div>
      </Modal>

      <Modal open={!!created} onClose={() => setCreated(null)} title={`Connect · ${created?.name}`} width={680}>
        {created ? <KeySetup k={created} /> : null}
      </Modal>

      <Modal
        open={!!renaming}
        onClose={() => setRenaming(null)}
        title="Rename key"
        footer={
          <Button
            variant="primary"
            disabled={!renaming?.name.trim()}
            onClick={async () => {
              if (renaming && (await attempt(() => reducers().renameMcpKey({ id: renaming.id, name: renaming.name })))) setRenaming(null);
            }}
          >
            Save
          </Button>
        }
      >
        <Input autoFocus maxLength={80} value={renaming?.name ?? ''} onChange={(e) => setRenaming((r) => (r ? { ...r, name: e.target.value } : r))} />
      </Modal>

      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete MCP key"
        danger
        confirmLabel="Delete key"
        message={`Assistants using "${deleting?.name}" lose access immediately.`}
        onConfirm={() => attempt(() => reducers().deleteMcpKey({ id: deleting!.id }), 'Key deleted')}
      />
    </Card>
  );
}
